import axios from 'axios';
import { API } from '../../../backend';
import { useAppContext } from '../../../hooks/context/AppContext';
import { useState, useMemo } from 'react';
import useSilentIdTokenRefresh from "../../../msal/useSilentTokenRefresh";



// Common error handler
const handleError = (error: unknown, context: string) => {
  console.error(`Error ${context}:`, error);
  return null;
};

// Milestone interfaces
export interface MilestonePayload {
  Product_Lock: string;
  Product_Lock_Date: string;
  BCSignOff_CP: string;
  BCSignOff_CP_Date: string;
  MPTProductValidation: string;
  MPTProductValidation_Date: string;
  FirstProduction: string;
  FirstProduction_Date: string;
  SIW: string;
  Comment: string;
  Risk_Key: string;
};

// Custom hook for API client
export const useApiClient =  () => {
  const { authData } = useAppContext();
  const [idToken, setIdToken] = useState(null);
  const getIdTokenSilently = useSilentIdTokenRefresh((token: any | null, account: any | null) => {
    setIdToken(token)
    
  });  
  const apiClient = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      headers: {
        'Content-Type': 'application/json',
        Authorization:  idToken ? `Bearer ${idToken}` :  `Bearer ${authData.idToken}`,
      },
    });

     // Add response interceptor to handle 401 errors
     instance.interceptors.response.use(
      response => response,
      async (error) => {
        const originalRequest = error.config;

        // If the error is 401 and we haven't already retried
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const {token}  = await getIdTokenSilently();
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            console.error('Error refreshing token:', refreshError);
            // If token refresh fails, redirect to login or handle as needed
            // You might want to add your auth redirect logic here
            window.location.reload();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    
    return instance;
  }, [idToken, authData.idToken]);


  // Risk API
  const getRisk = async () => {
    try {
      const response = await apiClient.get('/risks');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching risks');
    }
  };

  // Week API
  const getWeek = async (year: string, quarter: string) => {
    try {
      let url = '/launchWeek';
      
      if (year && quarter) {
        url = `/launchWeek?year=${year}&quarter=${quarter}`;
      } else if (year) {
        url = `/launchWeek?year=${year}`;
      } else if (quarter) {
        url = `/launchWeek?quarter=${quarter}`;
      }
      
      const response = await apiClient.get(url);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching risks');
    }
  };
  
  // Milestone API
  const createMilestone = async (payload: MilestonePayload, project_key: string) => {
    try {
      const response = await apiClient.post(`/milestone/${project_key}`, payload);
      return response.data;
    } catch (err) {
      return handleError(err, 'creating milestone');
    }
  };

  const updateMilestone = async (payload: MilestonePayload, project_key: string) => {
    try {
      const response = await apiClient.put(`/milestone/${project_key}`, payload);
      return response.data;
    } catch (err) {
      return handleError(err, 'updating milestone');
    }
  };

  const getMilestone = async (project_key: string) => {
    try {
      const response = await apiClient.get(`/milestone/${project_key}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching milestone');
    }
  };

  return {
    getRisk,
    createMilestone,
    updateMilestone,
    getMilestone,
    getWeek
  };
};